// Copyright (c) 2024-2026 AlphaGrade. All Rights Reserved.
// Proprietary and confidential. See LICENSE file in project root.
import React from 'react';
import './RegimeIndicator.css';

/**
 * RegimeIndicator — a compact, always-visible "market conditions" bar that sits
 * at the top of the portfolio results so the macro backdrop is the first thing a
 * user sees. Makes it obvious that a soft environment (not their stock picks) is
 * what's capping conviction/action across the board.
 */
const REGIME = {
  risk_on:  { label: 'Risk-On',  color: '#22c55e', desc: 'Tailwinds — supportive for risk-taking' },
  neutral:  { label: 'Neutral',  color: '#eab308', desc: 'Mixed — balanced positioning' },
  risk_off: { label: 'Risk-Off', color: '#f97316', desc: 'Headwinds — favors caution & quality' },
};

const TURB_COLOR = {
  Calm: '#22c55e', Choppy: '#eab308', Turbulent: '#f97316', Crisis: '#ef4444',
};

function normRegime(r) {
  const s = (r || '').toString().toLowerCase();
  if (s.includes('risk_on') || s.includes('risk-on') || s.includes('riskon')) return 'risk_on';
  if (s.includes('risk_off') || s.includes('risk-off') || s.includes('riskoff')) return 'risk_off';
  return 'neutral';
}

export default function RegimeIndicator({ regime, turbulence }) {
  if (!regime && !turbulence) return null;

  const r = REGIME[normRegime(regime)];
  const turbLabel = turbulence?.turbulence_label;
  const turbColor = turbLabel ? (TURB_COLOR[turbLabel] || '#94a3b8') : null;
  const dampened =
    turbulence && typeof turbulence.dampener === 'number' && turbulence.dampener < 1.0;

  return (
    <div className="regime-indicator" role="status" aria-label="Current market conditions">
      <span className="ri-title">Market conditions</span>
      <span className="ri-pill" style={{ borderColor: r.color, color: r.color }}>
        <span className="ri-dot" style={{ background: r.color }} />
        {r.label}
      </span>
      {turbLabel && (
        <span className="ri-pill" style={{ borderColor: turbColor, color: turbColor }}>
          <span className="ri-dot" style={{ background: turbColor }} />
          {turbLabel}
        </span>
      )}
      <span className="ri-note">
        {r.desc}
        {dampened ? ' · Setup & Convex scores dampened market-wide right now' : ''}
      </span>
    </div>
  );
}
